// Alert emails, Stage 5 piece 2. Words: ALERT_EMAILS_WORDS.md, mockup MOCKUP_ALERT_EMAILS.html
// (approved by Robert 2026-10-10). Glove copy rule: option A (Robert, 2026-10-10).
//
// Four alerts:
// 1. LOTO date missing after Node 4, then a reported date waiting to verify.
// 2. Training not current: 30 days out, the day it expires, then monthly.
// 3. Glove pair due: 30 days out, then the day it expires and weekly.
// 4. The weekly JSP summary, Monday 6 AM.
//
// Every alert has a key. A key is sent once (alertSent/{key}), so a missed or repeated
// run never doubles an email and a missed day catches up on the next run. Repeats
// carry the period in the key: w0, w1 for weekly, m0, m1 for monthly.
//
// Wording rule (Robert, 2026-10-10): no he, she, his or her. Use the name, "the trainee" or "the QEW".
//
// Pure: data in, data out. Shared with the server and unit tested.

import { lotoEverVerified, usDate } from "./loto";
import { NODES, type NodeId } from "./training";
import { dueOf, type GlovePair, type RetiredNote } from "./gloves";
import { awaitsLateAnswer, closedAtText, type CloseRow } from "./jsp-close";
import type { EmailRequest, Recipient } from "./email";

/** Days after Node 4 with no LOTO date before the first email. */
export const LOTO_MISSING_FIRST_DAY = 14;
/** From this day on, the site's Escalation contacts are added. */
export const LOTO_ESCALATE_DAY = 30;
/** A reported date waiting this long gets a weekly reminder. */
export const LOTO_VERIFY_REPEAT_DAYS = 7;
/** Training and gloves warn this many days ahead. */
export const WARN_DAYS = 30;
/** Expired training repeats this often. */
export const TRAINING_REPEAT_DAYS = 30;
/** Expired gloves repeat this often. */
export const GLOVE_REPEAT_DAYS = 7;
/** The JSP summary looks back this far for closed JSPs. */
export const SUMMARY_DAYS = 7;

export const REASON = {
  lotoMissing: "LOTO date missing",
  lotoVerify: "LOTO verify",
  trainingSoon: "Training due soon",
  trainingExpired: "Training expired",
  gloveSoon: "Glove Due Soon",
  gloveExpired: "Glove expired",
  jspSummary: "JSP summary",
} as const;

// ---- What the jobs read ----

export interface AlertPerson {
  qewId: string;
  name: string;
  email: string;
  siteNumber: string;
  isActive: boolean;
  lotoTrainedOn: string | null;
  lotoReportedOn: string | null;
  lotoVerifiedOn: string | null;
  lotoLastVerifiedOn: string | null;
}

export interface AlertRecord {
  qewId: string;
  nodeId: string;
  completedOn: string;
  expiresOn: string | null;
}

export interface AlertContact {
  name: string;
  email: string;
  scope: "site" | "program";
  siteNumber: string;
  isActive: boolean;
  usedFor: Record<string, boolean>;
}

export interface AlertSite {
  siteNumber: string;
  name: string;
}

/** One alert ready to go: its key and one email per recipient group. */
export interface PlannedAlert {
  key: string;
  emails: EmailRequest[];
}

/** What every alert needs to address its emails. */
export interface AlertWorld {
  companyName: string;
  sites: ReadonlyMap<string, AlertSite>;
  contacts: readonly AlertContact[];
}

// ---- Small helpers ----

export function dayDiff(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86_400_000);
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const hasEmail = (e: string | null | undefined) => typeof e === "string" && EMAIL.test(e.trim());

export function siteNameOf(world: AlertWorld, siteNumber: string): string {
  return world.sites.get(siteNumber)?.name || `Site ${siteNumber}`;
}

/** Site contacts with a tick, active, with an email. */
export function siteContactsFor(world: AlertWorld, siteNumber: string, tick: string): Recipient[] {
  return world.contacts
    .filter((c) => c.scope === "site" && c.siteNumber === siteNumber && c.isActive && c.usedFor[tick] === true && hasEmail(c.email))
    .map((c) => ({ name: c.name, email: c.email.trim() }));
}

/** Program contacts with a tick, active, with an email. */
export function programContactsFor(world: AlertWorld, tick: string): Recipient[] {
  return world.contacts
    .filter((c) => c.scope === "program" && c.isActive && c.usedFor[tick] === true && hasEmail(c.email))
    .map((c) => ({ name: c.name, email: c.email.trim() }));
}

/** One recipient group: who, what to call them, why they got it, and their button. */
interface Group {
  to: Recipient[];
  toLabel: string;
  footer: string;
  button: EmailRequest["button"];
  /** Overrides the shared wording for this group. */
  headline?: string;
  lines?: string[];
}

const SIGN = "Sent by SafeTag for the Electrical Safety Program.";

function footerOf(why: string, companyName: string): string {
  return `You get this because ${why}. ${companyName ? `Sent by SafeTag for the ${companyName} Electrical Safety Program.` : SIGN}`;
}

/**
 * One email per group, in order. A person already in an earlier group is dropped from
 * later ones, so nobody gets the same alert twice. Empty groups are skipped.
 */
function emailsFor(
  base: Omit<EmailRequest, "to" | "toLabel" | "footer" | "button">,
  groups: Group[],
  companyName: string,
): EmailRequest[] {
  const seen = new Set<string>();
  const out: EmailRequest[] = [];
  for (const g of groups) {
    const to = g.to.filter((r) => {
      const k = r.email.trim().toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    if (!to.length) continue;
    out.push({
      ...base,
      headline: g.headline ?? base.headline,
      lines: g.lines ?? base.lines,
      to,
      toLabel: g.toLabel,
      footer: footerOf(g.footer, companyName),
      button: g.button,
    });
  }
  return out;
}

const personAsRecipient = (p: AlertPerson): Recipient[] => (hasEmail(p.email) ? [{ name: p.name, email: p.email.trim() }] : []);
const dashboardButton = (label: string, qewId: string) => ({ label, path: `?tag=${encodeURIComponent(qewId)}` });

// ---- 1. LOTO ----

/** The day Node 4 was done, from the person's records. Null if not done. */
export function node4DoneOn(records: readonly AlertRecord[], qewId: string): string | null {
  const done = records.filter((r) => r.qewId === qewId && r.nodeId === "node4" && r.completedOn).map((r) => r.completedOn);
  return done.sort()[0] ?? null;
}

/**
 * 1a. Node 4 done, no LOTO date, not Active. Day 14, then weekly. Escalation from day 30.
 * Null when nothing is due.
 */
export function lotoMissingAlert(p: AlertPerson, node4On: string | null, today: string, world: AlertWorld): PlannedAlert | null {
  if (!node4On || p.isActive || p.lotoTrainedOn || lotoEverVerified(p)) return null;
  const days = dayDiff(node4On, today);
  if (days < LOTO_MISSING_FIRST_DAY) return null;
  const week = Math.floor((days - LOTO_MISSING_FIRST_DAY) / 7);
  const escalate = days >= LOTO_ESCALATE_DAY;
  const site = siteNameOf(world, p.siteNumber);
  const rows = [
    { label: "Trainee", value: `${p.name} · ${p.qewId}` },
    { label: "Node 4 done", value: usDate(node4On) },
    { label: "Days without a date", value: String(days) },
  ];
  const base = {
    siteNumber: p.siteNumber,
    siteName: site,
    companyName: world.companyName,
    subject: escalate ? `LOTO date still missing · ${days} days · ${p.name}` : `LOTO date needed · ${p.name}`,
    headline: "No LOTO date on record. The trainee cannot go Active.",
    lines: [`${p.name} finished Nodes 1 to 4. A LOTO training date is needed before the trainee can work as a QEW.`],
    rows,
    reason: REASON.lotoMissing,
    recordId: p.qewId,
  };
  const verifyButton = { label: "Open Training Record", path: `#/training/${p.siteNumber}/${encodeURIComponent(p.qewId)}` };
  const groups: Group[] = [
    {
      to: personAsRecipient(p),
      toLabel: p.name,
      footer: "your LOTO date is missing",
      button: dashboardButton("Enter LOTO Date", p.qewId),
      headline: "No LOTO date on record. You cannot go Active yet.",
      lines: ["You finished Nodes 1 to 4. Enter your LOTO training date so it can be verified."],
    },
    { to: siteContactsFor(world, p.siteNumber, "lotoVerifier"), toLabel: `${site} LOTO verifiers`, footer: `you are a LOTO Verifier at ${site}`, button: verifyButton },
  ];
  if (escalate) {
    groups.push({ to: siteContactsFor(world, p.siteNumber, "escalation"), toLabel: `${site} escalation`, footer: `you are an Escalation contact at ${site}`, button: verifyButton });
  }
  const emails = emailsFor(base, groups, world.companyName);
  return emails.length ? { key: `loto-missing-${p.qewId}-${node4On}-w${week}`, emails } : null;
}

/** 1b. A reported date waiting to verify. Right away, then weekly from day 7. */
export function lotoVerifyAlert(p: AlertPerson, today: string, world: AlertWorld): PlannedAlert | null {
  if (!p.lotoTrainedOn || p.lotoVerifiedOn || !p.lotoReportedOn) return null;
  const days = Math.max(0, dayDiff(p.lotoReportedOn, today));
  const week = days < LOTO_VERIFY_REPEAT_DAYS ? 0 : Math.floor(days / 7);
  const site = siteNameOf(world, p.siteNumber);
  const emails = emailsFor(
    {
      siteNumber: p.siteNumber,
      siteName: site,
      companyName: world.companyName,
      subject: week === 0 ? `LOTO date to verify · ${p.name}` : `LOTO date still waiting to verify · ${p.name}`,
      headline: "A LOTO date needs your verification.",
      lines: [`${p.name} reported a LOTO training date. Confirm it, or enter the right date.`],
      rows: [
        { label: "QEW", value: `${p.name} · ${p.qewId}` },
        { label: "Date reported", value: usDate(p.lotoTrainedOn) },
        { label: "Reported", value: usDate(p.lotoReportedOn) },
      ],
      reason: REASON.lotoVerify,
      recordId: p.qewId,
    },
    [
      {
        to: siteContactsFor(world, p.siteNumber, "lotoVerifier"),
        toLabel: `${site} LOTO verifiers`,
        footer: `you are a LOTO Verifier at ${site}`,
        button: { label: "Verify LOTO Date", path: `#/people/p/${encodeURIComponent(p.qewId)}/loto` },
      },
    ],
    world.companyName,
  );
  return emails.length ? { key: lotoVerifyKey(p.qewId, p.lotoReportedOn, p.lotoTrainedOn, week), emails } : null;
}

/** Shared by the report step (right away) and the daily job, so the first email goes once. */
export function lotoVerifyKey(qewId: string, reportedOn: string, trainedOn: string, week: number): string {
  return `loto-verify-${qewId}-${reportedOn}-${trainedOn}-w${week}`;
}

// ---- 2. Training ----

const nodeLabel = (nodeId: string) => {
  const n = NODES.findIndex((x) => x.nodeId === nodeId);
  return n < 0 ? nodeId : `Node ${n + 1} · ${NODES[n]!.nodeName}`;
};

/** The newest record of each node for one person. */
export function latestByNode(records: readonly AlertRecord[], qewId: string): AlertRecord[] {
  const best = new Map<string, AlertRecord>();
  for (const r of records) {
    if (r.qewId !== qewId || !r.completedOn) continue;
    const had = best.get(r.nodeId);
    if (!had || r.completedOn > had.completedOn) best.set(r.nodeId, r);
  }
  return [...best.values()];
}

/** 2. One node of an Active QEW. 30 days out, the day after the last good day, then monthly. */
export function trainingAlert(p: AlertPerson, r: AlertRecord, today: string, world: AlertWorld): PlannedAlert | null {
  if (!p.isActive || !r.expiresOn) return null;
  const left = dayDiff(today, r.expiresOn);
  const site = siteNameOf(world, p.siteNumber);
  const label = nodeLabel(r.nodeId);
  const rows = [
    { label: "QEW", value: `${p.name} · ${p.qewId}` },
    { label: "Node", value: label },
    { label: "Completed", value: usDate(r.completedOn) },
    { label: left >= 0 ? "Expires" : "Expired", value: usDate(r.expiresOn) },
  ];
  const record = { label: "Open Training Record", path: `#/training/${p.siteNumber}/${encodeURIComponent(p.qewId)}` };
  const qewGroup = (headline: string): Group => ({
    to: personAsRecipient(p),
    toLabel: p.name,
    footer: "this is your training record",
    button: dashboardButton("Open My Dashboard", p.qewId),
    headline,
    lines: ["Retake this node with your instructor."],
  });
  const alerts: Group = { to: siteContactsFor(world, p.siteNumber, "alerts"), toLabel: `${site} alerts`, footer: `you are an Alerts contact at ${site}`, button: record };

  if (left >= 0 && left <= WARN_DAYS) {
    const emails = emailsFor(
      {
        siteNumber: p.siteNumber,
        siteName: site,
        companyName: world.companyName,
        subject: `Training expires ${usDate(r.expiresOn)} · ${p.name}`,
        headline: `Training expires in ${left} ${left === 1 ? "day" : "days"}.`,
        lines: [`${p.name} needs to retake this node with an instructor before it expires.`],
        rows,
        reason: REASON.trainingSoon,
        recordId: p.qewId,
      },
      [qewGroup(`Your training expires in ${left} ${left === 1 ? "day" : "days"}.`), alerts],
      world.companyName,
    );
    return emails.length ? { key: `training-soon-${p.qewId}-${r.nodeId}-${r.expiresOn}`, emails } : null;
  }
  if (left < 0) {
    const month = Math.floor((-left - 1) / TRAINING_REPEAT_DAYS);
    const emails = emailsFor(
      {
        siteNumber: p.siteNumber,
        siteName: site,
        companyName: world.companyName,
        subject: `Training expired · ${p.name}`,
        headline: "Training expired. The JSP still opens, with a red pill.",
        lines: [`${p.name} needs to retake this node with an instructor.`],
        rows,
        reason: REASON.trainingExpired,
        recordId: p.qewId,
      },
      [
        qewGroup("Your training expired. Your JSP still opens, with a red pill."),
        alerts,
        { to: programContactsFor(world, "trainingAlerts"), toLabel: "Training alerts", footer: "you are a Training Alerts contact for the program", button: record },
      ],
      world.companyName,
    );
    return emails.length ? { key: `training-expired-${p.qewId}-${r.nodeId}-${r.expiresOn}-m${month}`, emails } : null;
  }
  return null;
}

// ---- 3. Gloves ----
// The glove card holds one pair per QEW: sealed in the bag or in service. Option A (Robert, 2026-10-10):
// the 30-day email always copies the site's Alerts contacts, so a pair gets ordered. Glove inventory comes later.

/** 3a. The pair on file is inside 30 days of its last good day. Once per pair. */
export function gloveSoonAlert(p: AlertPerson, pair: GlovePair, today: string, world: AlertWorld): PlannedAlert | null {
  if (!p.isActive || pair.status === "retired") return null;
  const due = dueOf(pair);
  const left = dayDiff(today, due);
  if (left < 0 || left > WARN_DAYS) return null;
  const site = siteNameOf(world, p.siteNumber);
  const sealed = pair.status === "sealed";
  const days = `${left} ${left === 1 ? "day" : "days"}`;
  const rows = [
    { label: "QEW", value: `${p.name} · ${p.qewId}` },
    { label: "Pair", value: `${pair.gloveClass} · Size ${pair.gloveSize}` },
    sealed ? { label: "Tested", value: usDate(pair.testedOn) } : { label: "Placed in service", value: usDate(pair.inServiceOn ?? "") },
    { label: sealed ? "Use by" : "Replace by", value: usDate(due) },
  ];
  const emails = emailsFor(
    {
      siteNumber: p.siteNumber,
      siteName: site,
      companyName: world.companyName,
      subject: `Glove pair expires ${usDate(due)} · ${p.name}`,
      headline: `${p.name}'s gloves expire in ${days}. Make sure a new pair is ordered.`,
      lines: [sealed ? "The sealed pair in the bag passes 12 months from its test date." : "The pair in service passes 6 months."],
      rows,
      reason: REASON.gloveSoon,
      recordId: pair.pairId,
    },
    [
      {
        to: personAsRecipient(p),
        toLabel: p.name,
        footer: "these are your gloves",
        button: dashboardButton("Open My Gloves", p.qewId),
        headline: `Your gloves expire in ${days}. Get a new pair ready.`,
        lines: [
          sealed
            ? "Your sealed pair passes 12 months from its test date. Put it in service before then, or get a new pair."
            : "Your pair in service passes 6 months. Record the new pair in the app when it arrives. The old pair retires then.",
        ],
      },
      { to: siteContactsFor(world, p.siteNumber, "alerts"), toLabel: `${site} alerts`, footer: `you are an Alerts contact at ${site}`, button: { label: "Open Gloves", path: "#/gloves" } },
    ],
    world.companyName,
  );
  return emails.length ? { key: `glove-soon-${pair.pairId}-${due}`, emails } : null;
}

/** 3b. The app retired the pair on its clock and nothing replaced it. The day after, then weekly. */
export function gloveExpiredAlert(p: AlertPerson, retired: RetiredNote, today: string, world: AlertWorld): PlannedAlert | null {
  if (!p.isActive || !retired.removedOn || !retired.removalReason.startsWith("Expired")) return null;
  const since = dayDiff(retired.removedOn, today);
  if (since < 1) return null;
  const week = Math.floor((since - 1) / GLOVE_REPEAT_DAYS);
  const site = siteNameOf(world, p.siteNumber);
  const gloves = { label: "Open Gloves", path: "#/gloves" };
  const emails = emailsFor(
    {
      siteNumber: p.siteNumber,
      siteName: site,
      companyName: world.companyName,
      subject: `Glove pair expired · ${p.name}`,
      headline: `${p.name} has no gloves in service.`,
      lines: ["The app retired the pair on its clock. A new pair needs to be recorded."],
      rows: [
        { label: "QEW", value: `${p.name} · ${p.qewId}` },
        { label: "Pair", value: retired.pairId },
        { label: "Last good day", value: usDate(retired.removedOn) },
        { label: "Days without gloves", value: String(since) },
      ],
      reason: REASON.gloveExpired,
      recordId: retired.pairId,
    },
    [
      {
        to: personAsRecipient(p),
        toLabel: p.name,
        footer: "these are your gloves",
        button: dashboardButton("Open My Gloves", p.qewId),
        headline: "Your gloves expired. Record a new pair when it arrives.",
        lines: ["The app retired your pair on its clock."],
      },
      { to: siteContactsFor(world, p.siteNumber, "alerts"), toLabel: `${site} alerts`, footer: `you are an Alerts contact at ${site}`, button: gloves },
      { to: programContactsFor(world, "jspAlerts"), toLabel: "JSP alerts", footer: "you are a JSP Alerts contact for the program", button: gloves },
    ],
    world.companyName,
  );
  return emails.length ? { key: `glove-expired-${retired.pairId}-w${week}`, emails } : null;
}

// ---- 4. The weekly JSP summary ----

export interface SummaryJsp extends CloseRow {
  qewId: string;
  siteNumber: string;
}

/**
 * 4. One QEW at one site. Waiting first (open, or auto-closed and still waiting for the
 * close answer), then closed in the last 7 days. Null: nothing to say, no email.
 */
export function jspSummaryAlert(p: AlertPerson, siteNumber: string, jsps: readonly SummaryJsp[], today: string, nowIso: string, world: AlertWorld): PlannedAlert | null {
  if (!hasEmail(p.email)) return null;
  const mine = jsps.filter((j) => j.qewId === p.qewId && j.siteNumber === siteNumber);
  const waiting = mine.filter((j) => j.status === "open" || awaitsLateAnswer(j, nowIso)).sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
  const cutoff = Date.parse(nowIso) - SUMMARY_DAYS * 86_400_000;
  const closed = mine
    .filter((j) => !waiting.includes(j) && j.status === "closed" && j.closedAt && Date.parse(j.closedAt) >= cutoff)
    .sort((a, b) => (b.closedAt ?? "").localeCompare(a.closedAt ?? ""));
  if (!waiting.length && !closed.length) return null;
  const site = siteNameOf(world, siteNumber);
  const name = (j: SummaryJsp) => `JSP ${j.jspNumber} · ${j.assetId}`;
  const rows = [
    ...waiting.map((j) => ({ label: name(j), value: j.status === "open" ? `Open · ${j.taskName}` : "Waiting For Your Answer" })),
    ...closed.map((j) => ({ label: name(j), value: `${j.isAutoClosed ? "Auto-closed" : "Closed"} ${closedAtText(j.closedAt ?? "")}` })),
  ];
  const w = waiting.length;
  const emails = emailsFor(
    {
      siteNumber,
      siteName: site,
      companyName: world.companyName,
      subject: w ? `Your JSPs this week · ${w} waiting` : "Your JSPs this week",
      headline: w ? `You have ${w} JSP${w === 1 ? "" : "s"} waiting. Tell us how you left the equipment.` : "No JSPs waiting. Here is what you closed this week.",
      lines: [
        w ? "Open your Dashboard. The close card asks how you left it." : "Nothing needs doing.",
        ...(closed.some((j) => j.isAutoClosed) ? ["Auto-closed means the app closed it after 12 hours."] : []),
      ],
      rows,
      reason: REASON.jspSummary,
      recordId: p.qewId,
    },
    [
      {
        to: personAsRecipient(p),
        toLabel: p.name,
        footer: `you had JSPs at ${site} in the last ${SUMMARY_DAYS} days`,
        button: dashboardButton("Open My Dashboard", p.qewId),
      },
    ],
    world.companyName,
  );
  return emails.length ? { key: `jsp-summary-${p.qewId}-${siteNumber}-${today}`, emails } : null;
}

/** The nodes that can expire. Node 4 does not. */
export const EXPIRING_NODES: readonly NodeId[] = NODES.filter((n) => n.validDays !== null).map((n) => n.nodeId);
